import "./polyfills";

import React from "react";
import ReactDom from "react-dom";

import createRedux, {Provider} from "./store";

import App from "./app.js";

import fetch from "./lib/fetch";
import deepEqual from "lib/deepEqual";
import flattenValues from "lib/flattenValues";
import * as history from "./lib/history";

import "../bootstrap.scss";
import "./styles/main.css";

history.migrateUrl();

let {urlparams, state: urlstate} = history.parse(window.location);

let defaultOpts = {
    embedded: window.self !== window.top || void 0,
    config: "./config.json",
    selected: 0,
    exposed: true
};

const tryToParseAsJSON = str => {
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
};

let loadConfigs = async (opts) => {
    let mainconf = {};

    try {
        let req = await fetch(opts.config);
        mainconf = req.response;
        delete opts.config;
    } catch (err) {
        //skip
    }

    let results = {...mainconf, ...opts};
    delete results.config;

    for (let key of ["charts", "colors", "datasets"]) {
        results[key] = tryToParseAsJSON(results[key]);
        if (typeof results[key] === "string") {
            let {response, responseURL} =  await fetch(results[key]);
            results[key + ".url"] = responseURL;
            results[key] = response;
        }
    }

    // handle multiple aggregator configs
    if (typeof results.datasets === "object" && !Array.isArray(results.datasets)) {
        let datasets = {};
        for (let key of Object.keys(results.datasets)) {
            let {response, responseURL} =  await fetch(results.datasets[key]);
            datasets[responseURL] = response.map(dataset => {
                return {...dataset, name: key + ": " + dataset.name};
            });
        }

        results.datasets = datasets;
    }

    return results;
};

loadConfigs({...defaultOpts, ...urlparams})
    .then(function(options) {
        const initialState = {...options, ...urlstate};

        const redux = createRedux();
        const {actions} = redux;

        const colors = options.colors || {};

        const enableTouchControls = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

        if (enableTouchControls) {
            document.documentElement.classList.add("touch-controls");
            window.scrollTo(0, 0);
        }

        let props = {
            actions,
            colors,
            embedded: options.embedded,
            touchControls: enableTouchControls,
            timezone: options.timezone !== void 0 ? options.timezone : new Date().getTimezoneOffset()
        };

        const loadState = state => {
            const normalizeChart = function(conf) {
                conf.dataset = conf.dataset || conf.url || conf.dataid;
                delete conf.url;
                delete conf.dataid;
                return flattenValues(conf);
            };

            actions.loadState({
                selected: state.selected,
                exposed: state.exposed,
                charts: (state.charts || []).map(normalizeChart)
            });
        };

        loadState(initialState);

        let insertPlaceholders = (datasets, base) => {
            datasets.forEach(dataset => {
                actions.insertPlaceholderDataset(dataset, base);
            });
        };

        if (Array.isArray(options.datasets)) {
            insertPlaceholders(options.datasets, options["datasets.url"]);
        } else if (typeof options.datasets == "object") {
            for (let base of Object.keys(options.datasets)) {
                insertPlaceholders(options.datasets[base], base);
            }
        }

        ReactDom.render(
            <Provider store={redux}>
                <App {...props} />
            </Provider>,
            document.querySelector("#app")
        );

        if (options.embedded) {
            document.documentElement.classList.add("embedded");
        } else {
            window.addEventListener("popstate", function() {
                let {state} = history.parse(window.location);
                loadState({...options, ...state});
            });

            let urlNotYetUpdated = true;

            const isShallowEqual = (obj1, obj2) => {
                let keys = Object.keys(obj1);
                return keys.length === Object.keys(obj2).length && keys.every((k) => obj1[k] === obj2[k]);
            };

            redux.observe((prevState, nextState) => {
                if (!isShallowEqual(prevState, nextState)) {
                    let {charts} = nextState;
                    let {exposed, selected} = nextState.viewer;

                    let initialCharts = (options.charts || []).map(history.cleanChartConf);
                    if (deepEqual(initialCharts, charts.map(history.cleanChartConf))) {
                        charts = void 0;
                    }

                    if (options.exposed === exposed) {
                        exposed = void 0;
                    }

                    if (options.selected === selected) {
                        selected = void 0;
                    }

                    history.save({charts, exposed, selected}, urlNotYetUpdated);
                    urlNotYetUpdated = false;
                }

                //update url only when charts or viewer changes
            }, ({charts, viewer}) => ({charts, viewer}));
        }

        // disable animation while window is resizing to prevent
        // css width/height transitions being applied on chart-container
        window.addEventListener("resize", () => {
            let el = document.documentElement;
            el.classList.add("noanimation");
            setTimeout(() => {
                el.classList.remove("noanimation");
            }, 0);
        });

        document.documentElement.classList.add("ready");
    });



// WEBPACK FOOTER //
// ./src/kpi/index.js